import { Controller } from "@hotwired/stimulus"

// Editor for one model-level work-time rules dialog form: adds/removes
// break-rule threshold rows (plain inputs, NEW_RECORD placeholder like the
// seasonal break editor) and shows the mode-dependent sections — thresholds
// for validate/auto_deduct, the action select for validate only, the min/max
// fields of the limits form for mode "active", the booking-window fields for
// mode "active", and the time-account fields for mode "active". The break,
// limits, booking and account targets are optional because each dialog form
// carries only its own section.
export default class extends Controller {
  static targets = [
    "modeSelect",
    "validateOnly",
    "autoDeductOnly",
    "thresholdsSection",
    "comparisonSection",
    "comparisonSelect",
    "thresholdAfterLabel",
    "template",
    "rows",
    "limitsModeSelect",
    "limitsFields",
    "limitActionSelect",
    "reasonFieldSection",
    "bookingModeSelect",
    "bookingFields",
    "accountModeSelect",
    "accountFields",
  ]

  connect() {
    this.apply()
  }

  modeChanged() {
    this.apply()
  }

  comparisonChanged() {
    this.syncThresholdLabels()
  }

  addThreshold(event) {
    event.preventDefault()
    const html = this.templateTarget.innerHTML.replaceAll("NEW_RECORD", new Date().getTime())
    this.rowsTarget.insertAdjacentHTML("beforeend", html)
    // The template still carries the label it was rendered with, so a row added
    // after the comparison was switched would contradict the select above it.
    this.syncThresholdLabels()
  }

  // "Ab mehr als 6,0 Std." and "Ab 6,0 Std." are the same threshold read two
  // ways, so the row label follows the select rather than stating one of them
  // until the dialog is reopened. Both texts ride on the span (the controller
  // sits on the form, not on this section, so Stimulus values would have to be
  // declared a level up from where they are used).
  syncThresholdLabels() {
    if (!this.hasComparisonSelectTarget) return

    const atLeast = this.comparisonSelectTarget.value === "at_least"
    this.thresholdAfterLabelTargets.forEach((label) => {
      const text = atLeast ? label.dataset.atLeastLabel : label.dataset.greaterThanLabel
      if (text) label.textContent = text
    })
  }

  removeThreshold(event) {
    event.preventDefault()
    event.target.closest("[data-work-time-rules-target='row']")?.remove()
  }

  apply() {
    if (this.hasModeSelectTarget) {
      const mode = this.modeSelectTarget.value
      const configurable = mode === "validate" || mode === "auto_deduct"
      this.toggleSection(this.thresholdsSectionTarget, configurable)
      this.toggleSection(this.validateOnlyTarget, mode === "validate")
      if (this.hasComparisonSectionTarget) {
        this.toggleSection(this.comparisonSectionTarget, configurable)
      }
      // Only auto_deduct ever writes the break, so only it can take a top-up
      // back — the server rejects the flag on any other mode, which means it
      // must not be submitted there either (toggleSection disables the input).
      if (this.hasAutoDeductOnlyTarget) {
        this.toggleSection(this.autoDeductOnlyTarget, mode === "auto_deduct")
      }
      this.syncThresholdLabels()
    }
    if (this.hasLimitsModeSelectTarget) {
      const limitsActive = this.limitsModeSelectTarget.value === "active"
      this.toggleSection(this.limitsFieldsTarget, limitsActive)
      // The reason field belongs to the "explain" action alone — the server
      // rejects it on any other action, so it must not be submitted otherwise.
      if (this.hasReasonFieldSectionTarget) {
        const explaining = this.limitActionSelectTargets.some(
          (select) => select.value === "explain",
        )
        this.toggleSection(this.reasonFieldSectionTarget, limitsActive && explaining)
      }
    }
    if (this.hasBookingModeSelectTarget) {
      this.toggleSection(this.bookingFieldsTarget, this.bookingModeSelectTarget.value === "active")
    }
    if (this.hasAccountModeSelectTarget) {
      this.toggleSection(this.accountFieldsTarget, this.accountModeSelectTarget.value === "active")
    }
  }

  toggleSection(container, active) {
    container.classList.toggle("hidden", !active)
    container.querySelectorAll("input, select").forEach((element) => {
      element.disabled = !active
    })
  }
}
